/**
 * 指令管理器：把"用户想执行一条 MAV_CMD"变成"飞控确认了 / 明确没确认"。
 *
 * ## 一、`confirmation` 是重传计数，不是"确认过没有"
 *
 * `COMMAND_LONG.confirmation` 的语义是**重传次数**：首次下发填 0，每次重发 +1。
 * 对端靠它区分"这是重传"和"用户又按了一次" —— 写错的表现是**偶发地执行两次**
 * （返航发两遍、解锁发两遍），在真机上后果严重。所以这里由管理器统一维护，
 * 调用方永远不需要（也不能）自己填这个字段。
 *
 * 注意会话层的 `MavlinkSession.sendCommandLong()` 把这个字段固定填 0，
 * 所以管理器**不能用它**发指令，必须自己组 `COMMAND_LONG` 帧。
 *
 * `COMMAND_INT` **没有** `confirmation` 字段（上游定义如此）。这是"精度 vs 重传语义"
 * 的取舍，理由写在 `gate.ts` 的 `evaluateGate` 里：带经纬度的命令优先精度。
 *
 * ## 二、`IN_PROGRESS` 不是失败，长任务会主动让出队列
 *
 * `MAV_RESULT_IN_PROGRESS` 表示"正在执行、后面还会回报"。收到它之后：
 * - **不**按 `ackTimeoutMs` 判失败，改为等后续 ACK（可能带 `progress` 百分比）；
 * - 立刻**让出串行槽**：校准要跑几十秒，期间用户还得能改模式、发别的命令；
 * - 不再重传：重传一条"正在执行"的命令在真机上可能再触发一次（再来一次校准）；
 * - 但仍有一个绝对上限（`longTaskTimeoutMs`，默认 15 分钟），否则链路中途断掉时
 *   界面上那条进度条会一直转下去。到上限时的结论是 `in-progress` 而**不是** `failed`：
 *   命令可能还在飞机上跑，说"失败"是在编造事实。
 *
 * ## 三、串行 + 去重
 *
 * `COMMAND_ACK` 里**只有 `command` 字段**，没有请求 id。所以：
 * - 同时飞两条同编号的命令，谁也说不清某一帧 ACK 属于哪一次 → 串行排队，
 *   并且同编号命令正在飞时，**参数不同**的新请求会被门禁直接拦下（`command-busy`）；
 * - 同编号同参数的重复请求（界面上按钮被连点两下）返回**同一个 Promise**，
 *   既不排队也不多发一帧。
 *
 * ## 四、`dispose()` 之后的结论不是"失败"
 *
 * 销毁时仍挂着或正在飞的命令会被置为 `disposed`：结果不再被跟踪，
 * 但**飞控可能已经执行了它**。把这种情况说成"失败"会让操作员以为飞机没动，
 * 这是最危险的一种谎报。
 *
 * ## 五、"为什么不让我干"要从 `STATUSTEXT` 里捡回来
 *
 * `COMMAND_ACK.result` 只能表达"失败了"，**说不了为什么**。具体原因通常是同一时刻
 * 单独播报的一条 `STATUSTEXT`（已核实的例子：直升机改模式时油门 runup 没做完，
 * `get_spool_state()` 是 `AP_Motors` 内部状态，任何遥测消息里都没有）。
 * 更麻烦的是这条文本在线上通常**早于** ACK —— ArduPilot 是在同一个处理函数里
 * `send_text()` 之后再 `return false`，上层才发 ACK。
 *
 * 所以失败结论会**晚一小段**兑现：前 400ms / 后 300ms 内的状态文本会被挂到这次结果上
 * （见 `openReasonWindow` 与 `types.ts` 的 `CommandReason`），界面上就能显示
 * 「执行失败：runup not complete」而不是干巴巴一个"失败"。成功路径不受影响。
 */

import { commandId, commandOf, degToLatLonE7, needsConfirmation, type CommandMeta } from '@hgc/ardupilot';
import { decodePayload, enumValue, messageById, messageIdOf, toNumber } from '@hgc/mavlink';
import type { MavlinkFrame } from '@hgc/mavlink';

import { createEventBus, type EventBus, type Unsubscribe } from '../events';
import type { CommandAckEvent, StatustextEntry } from '../vehicle';
import { evaluateGate, vehicleKindOfState } from './gate';
import { COMMAND_STATUS_LABELS, mavResultInfo, mavResultName, type CommandStatus } from './results';
import {
  DEFAULT_COMMAND_TIMERS,
  type AltitudeFrame,
  type CommandBlocker,
  type CommandGateResult,
  type CommandHistoryEntry,
  type CommandManagerEvents,
  type CommandManagerOptions,
  type CommandQueueEntry,
  type CommandQueueSnapshot,
  type CommandQueueState,
  type CommandReason,
  type CommandRequest,
  type CommandResult,
  type CommandTimers,
  type CommandVehicle,
} from './types';

/* ────────────────────────────── 默认值与常量 ────────────────────────────── */

const DEFAULT_ACK_TIMEOUT_MS = 3000;
const DEFAULT_LONG_COMMAND_ACK_TIMEOUT_MS = 10_000;
const DEFAULT_LONG_TASK_TIMEOUT_MS = 15 * 60_000;
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_MAX_TEMPORARY_RETRIES = 3;
const DEFAULT_HISTORY_LIMIT = 50;
/** `AUTOPILOT_VERSION` 请求的节流窗口：能力位不会变，问得太勤只是占链路 */
const CAPABILITIES_REQUEST_THROTTLE_MS = 10_000;
/**
 * 失败前后各留多长的窗口收集状态文本（见 `CommandManagerOptions` 里的详细理由）。
 *
 * `statustextLookbackMs` 之所以必须存在：ArduPilot 是在同一个处理函数里
 * **先发状态文本、后发 COMMAND_ACK** 的，原因在线上通常早于 ACK 到达。
 * 只向后看会漏掉最常见的形态 —— 那样这个功能等于没做。
 */
const DEFAULT_STATUSTEXT_LOOKBACK_MS = 400;
const DEFAULT_STATUSTEXT_GRACE_MS = 300;
/** 最近留下的状态文本条数（跨命令共享的环形缓冲，用于"向前关联"） */
const STATUSTEXT_BUFFER_LIMIT = 8;
/**
 * 允许作为失败原因的最大 `MAV_SEVERITY`。
 *
 * `MAV_SEVERITY` 越小越严重（0=EMERGENCY … 6=INFO、7=DEBUG）。
 * INFO/DEBUG 是例行播报（"Flight mode: LOITER"、"Armed"），把它们当原因只会制造噪声；
 * NOTICE(5) 及以上才可能是"为什么不让我这么干"。刻意**不**收紧到 WARNING(4)：
 * 有些固件把拒绝理由报成 NOTICE，那样会漏掉真正有用的信息。
 */
const REASON_MAX_SEVERITY = 5;
/** `int32` 的"无效/用当前值"哨兵（上游 `INT32_MAX`）。坐标缺失时按协议这么填。 */
const INT32_INVALID = 2 ** 31 - 1;

/**
 * 哪些结论值得等一句解释。
 *
 * 判据是"飞控主动拒绝了某件事" —— 这些场合 ArduPilot 基本都会配一条状态文本。
 * `accepted` **不**在内：成功路径不能被拖慢 300ms，而且成功也没什么好解释的。
 * `blocked`/`send-error`/`disposed` 是本地结论，帧根本没被飞控处理，等不来文本。
 */
const REASON_CORRELATED: ReadonlySet<CommandStatus> = new Set<CommandStatus>([
  'denied',
  'unsupported',
  'failed',
  'temporarily-rejected',
  'cancelled',
  'frame-unsupported',
  'not-in-control',
  'unknown-result',
  'no-ack',
]);

/**
 * 已知"下单快、干活慢"的命令。
 *
 * 判据是 ArduPilot 的行为：它对这类命令常常立刻回 `ACCEPTED`，然后自己跑几十秒，
 * 期间既不发 `IN_PROGRESS` 也不发第二条 ACK。若按普通命令处理，这条命令会一直
 * 占着串行槽（"终态"其实已经到了，只是活儿没干完），于是"校准的时候连改模式都点不动"。
 *
 * 所以对它们做两件事：发送成功后立刻让出串行槽，并把首次等确认的窗口放宽，
 * 少发一串无意义的重复帧。**不**把它们直接当长任务：`ACCEPTED` 就是终态，
 * 界面该显示成功（这也是 `DO_START_MAG_CAL` 在真机上的实际行为）。
 */
const KNOWN_LONG_RUNNING: ReadonlySet<number> = new Set(
  [
    'MAV_CMD_PREFLIGHT_CALIBRATION',
    'MAV_CMD_ACCELCAL_VEHICLE_POS',
    'MAV_CMD_DO_START_MAG_CAL',
    'MAV_CMD_DO_ACCEPT_MAG_CAL',
    'MAV_CMD_FIXED_MAG_CAL',
    'MAV_CMD_FIXED_MAG_CAL_YAW',
    'MAV_CMD_FIXED_MAG_CAL_FIELD',
    'MAV_CMD_PREFLIGHT_STORAGE',
    'MAV_CMD_RUN_PREARM_CHECKS',
    'MAV_CMD_NAV_ALTITUDE_WAIT',
  ]
    .map((name) => commandId(name))
    .filter((id): id is number => id !== undefined),
);

/** 高度基准 → `MAV_FRAME` 枚举名。查不到时 `mavFrameValue` 返回 `null`。 */
const FRAME_BY_ALTITUDE: Readonly<Record<AltitudeFrame, string>> = {
  relative: 'MAV_FRAME_GLOBAL_RELATIVE_ALT_INT',
  absolute: 'MAV_FRAME_GLOBAL_INT',
  terrain: 'MAV_FRAME_GLOBAL_TERRAIN_ALT_INT',
};

let commandIntBit: number | null | undefined;

/** `MAV_PROTOCOL_CAPABILITY_COMMAND_INT` 的位值。查不到时返回 `null`（=不支持）。 */
function commandIntCapabilityBit(): number | null {
  if (commandIntBit === undefined) {
    commandIntBit = enumValue('MAV_PROTOCOL_CAPABILITY', 'MAV_PROTOCOL_CAPABILITY_COMMAND_INT');
  }
  return commandIntBit;
}

/** 高度基准 → `MAV_FRAME` 数值。不抄数字：转换表只在生成产物里。 */
function mavFrameValue(altitudeFrame: AltitudeFrame): number | null {
  return enumValue('MAV_FRAME', FRAME_BY_ALTITUDE[altitudeFrame]);
}

/* ────────────────────────────── 内部结构 ────────────────────────────── */

interface NormalizedParams {
  readonly values: readonly number[];
  /** 调用方是否显式给了这一位。用来区分"没给"与"给了 0"（0 是合法坐标） */
  readonly provided: readonly boolean[];
}

interface QueueItem {
  readonly id: number;
  /** 去重键：命令号 + 参数 + 用户是否强制 LONG */
  readonly key: string;
  readonly command: number;
  readonly meta: CommandMeta | null;
  readonly params: readonly number[];
  readonly provided: readonly boolean[];
  readonly frameValue: number | null;
  readonly timeoutMs: number;
  readonly maxRetries: number;
  readonly startedAt: number;
  readonly knownLong: boolean;
  /** 当前这一轮用哪种帧下发；收到 `COMMAND_INT_ONLY`/`COMMAND_LONG_ONLY` 会改它 */
  viaInt: boolean;
  /** 已发出的帧数（重传累加） */
  attempts: number;
  /** 下一帧要带的 `confirmation`（= 已重传次数） */
  confirmation: number;
  lastResult: CommandAckEvent | null;
  progress: number | null;
  /** 正在等 ACK 的挂起函数；不在等待时为 `null` */
  ackWaiter: ((ack: CommandAckEvent | null) => void) | null;
  timer: number | null;
  /** 是否占据串行槽 */
  slotHeld: boolean;
  /** 是否已回报 `IN_PROGRESS` */
  inProgress: boolean;
  /** 结论（可能因为等待失败原因而推迟兑现） */
  status: CommandStatus | null;
  /** 关联到的飞控状态文本（失败原因） */
  readonly reasons: CommandReason[];
  /** 已经得出结论并兑现过 Promise */
  concluded: boolean;
  done: boolean;
  resolve: (result: CommandResult) => void;
}

/** 收到一帧确认之后的下一步动作。 */
type AckAction =
  | { readonly kind: 'done' }
  | { readonly kind: 'wait' }
  | {
      readonly kind: 'correct-format';
      readonly viaInt: boolean;
      readonly note: string;
      readonly fallbackStatus: CommandStatus;
    }
  | { readonly kind: 'temp-retry' };

/** `check()`/`execute()` 共用的中间结果 */
interface CommandPlan {
  readonly meta: CommandMeta | null;
  readonly command: number | null;
  readonly params: NormalizedParams;
  readonly key: string;
  readonly altitudeFrame: AltitudeFrame;
  readonly frameValue: number | null;
  readonly gate: CommandGateResult;
}

const EMPTY_BLOCKERS: readonly CommandBlocker[] = Object.freeze([]);
const EMPTY_REASONS: readonly CommandReason[] = Object.freeze([]);

/**
 * 这些结论是**本地**下的，不是飞控给的：文案必须来自 {@link CommandManager.statusText}。
 * 拿最后一帧 ACK 的文案去描述它们会撒谎 —— 例如长任务失联时最后一帧是
 * `IN_PROGRESS`（"正在执行中，飞控会继续回报"），而事实是"已经很久联系不上了"。
 */
const LOCALLY_CONCLUDED: ReadonlySet<CommandStatus> = new Set<CommandStatus>([
  'no-ack',
  'send-error',
  'in-progress',
  'long-only',
  'int-only',
  'disposed',
  'blocked',
]);

function normalizeParams(params: readonly (number | undefined)[] | undefined): NormalizedParams {
  const values: number[] = [];
  const provided: boolean[] = [];
  for (let index = 0; index < 7; index += 1) {
    const value = params?.[index];
    // 未给出的位按协议约定填 0（"不使用"的参数写 0）；
    // 显式传入的 NaN 必须原样保留 —— 它是 MAVLink 里"用当前值"的合法写法。
    values.push(value === undefined ? 0 : value);
    provided.push(value !== undefined);
  }
  return { values, provided };
}

function paramKey(params: NormalizedParams): string {
  return params.values.map((value) => (Number.isNaN(value) ? 'NaN' : String(value))).join(',');
}

/**
 * 十进制度 → `×1e7` 整数。
 *
 * 坐标缺失（调用方没给，或显式给了 NaN）时返回协议的"无效/用当前值"哨兵
 * `INT32_MAX`，而**不是** 0：0 是赤道与本初子午线上的真实坐标，
 * 把它当"没有坐标"会让一条 `DO_REPOSITION` 飞到几内亚湾去。
 */
function scaledLatLon(deg: number, wasProvided: boolean): number {
  if (!wasProvided || !Number.isFinite(deg)) return INT32_INVALID;
  return degToLatLonE7(deg);
}

/* ────────────────────────────── CommandManager ────────────────────────────── */

export class CommandManager {
  readonly events: EventBus<CommandManagerEvents>;

  private readonly vehicle: CommandVehicle;
  private readonly now: () => number;
  private readonly timers: CommandTimers;
  private readonly ackTimeoutMs: number;
  private readonly longCommandAckTimeoutMs: number;
  private readonly longTaskTimeoutMs: number;
  private readonly maxRetries: number;
  private readonly maxTemporaryRetries: number;
  private readonly statustextLookbackMs: number;
  private readonly statustextGraceMs: number;
  private readonly historyLimit: number;
  private readonly enforceConfirmation: boolean;
  private readonly capabilitiesProvider: (() => number | null) | null;
  private readonly unsubscribes: Unsubscribe[] = [];

  private readonly queue: QueueItem[] = [];
  /** 占据串行槽的命令（同时最多一条） */
  private active: QueueItem | null = null;
  /** 已发出但已让出串行槽的命令，按命令号索引（ACK 路由用） */
  private readonly awaiting = new Map<number, QueueItem>();
  private readonly pending = new Map<string, Promise<CommandResult>>();
  private readonly records: CommandHistoryEntry[] = [];
  /** 最近的状态文本，供"结论在即"的命令向前关联原因 */
  private readonly recentStatustext: CommandReason[] = [];
  /** 正在等失败原因的命令（每个都挂着一个到点收尾的定时器） */
  private readonly reasonWindows: { readonly item: QueueItem; readonly handle: number }[] = [];

  private capabilityBits: number | null = null;
  private capabilitiesRequestedAt: number | null = null;
  private readonly autopilotVersionMessageId: number | null;
  private nextId = 1;
  private disposed = false;

  constructor(options: CommandManagerOptions) {
    this.vehicle = options.vehicle;
    this.now = options.now ?? (() => Date.now());
    this.timers = options.timers ?? DEFAULT_COMMAND_TIMERS;
    this.ackTimeoutMs = options.ackTimeoutMs ?? DEFAULT_ACK_TIMEOUT_MS;
    this.longCommandAckTimeoutMs =
      options.longCommandAckTimeoutMs ?? DEFAULT_LONG_COMMAND_ACK_TIMEOUT_MS;
    this.longTaskTimeoutMs = options.longTaskTimeoutMs ?? DEFAULT_LONG_TASK_TIMEOUT_MS;
    this.maxRetries = options.maxRetries ?? DEFAULT_MAX_RETRIES;
    this.maxTemporaryRetries = options.maxTemporaryRetries ?? DEFAULT_MAX_TEMPORARY_RETRIES;
    this.statustextLookbackMs = options.statustextLookbackMs ?? DEFAULT_STATUSTEXT_LOOKBACK_MS;
    this.statustextGraceMs = options.statustextGraceMs ?? DEFAULT_STATUSTEXT_GRACE_MS;
    this.historyLimit = options.historyLimit ?? DEFAULT_HISTORY_LIMIT;
    this.enforceConfirmation = options.enforceConfirmation ?? true;
    this.capabilitiesProvider = options.capabilities ?? null;
    this.autopilotVersionMessageId = messageIdOf('AUTOPILOT_VERSION') ?? null;
    this.events = createEventBus<CommandManagerEvents>(
      options.onListenerError === undefined ? {} : { onListenerError: options.onListenerError },
    );

    this.unsubscribes.push(this.vehicle.events.on('commandAck', (event) => this.handleAck(event)));
    this.unsubscribes.push(this.vehicle.events.on('frame', (frame) => this.handleFrame(frame)));
    // 状态文本是"命令被拒绝的具体原因"的唯一来源，见 types.ts 的 CommandReason
    this.unsubscribes.push(
      this.vehicle.events.on('statustext', (event) => this.handleStatustext(event)),
    );
  }

  /* ────────────────────── 只读视图 ────────────────────── */

  /** `${systemId}:${componentId}`，与 `Vehicle.id` 一致 */
  get vehicleId(): string {
    return `${this.vehicle.systemId}:${this.vehicle.componentId}`;
  }

  get isDisposed(): boolean {
    return this.disposed;
  }

  /** 当前已知的协议能力位；还没收到 `AUTOPILOT_VERSION` 时为 `null` */
  capabilities(): number | null {
    if (this.capabilitiesProvider !== null) return this.capabilitiesProvider();
    return this.capabilityBits;
  }

  /** 飞控是否支持 `COMMAND_INT`（带经纬度的命令能否做到逐位无损） */
  supportsCommandInt(): boolean {
    const bit = commandIntCapabilityBit();
    const bits = this.capabilities();
    return bit !== null && bits !== null && (bits & bit) !== 0;
  }

  /** 当前队列：在飞、排队中、以及已让出槽位仍在等确认的 */
  queueSnapshot(): CommandQueueSnapshot {
    return Object.freeze({
      active: this.active === null ? null : this.describeItem(this.active),
      queued: Object.freeze(this.queue.map((item) => this.describeItem(item))),
      awaitingAck: Object.freeze([...this.awaiting.values()].map((item) => this.describeItem(item))),
    });
  }

  /** 最近 N 条命令的结论（新 → 旧）。默认 50 条，见 `historyLimit`。 */
  history(): readonly CommandHistoryEntry[] {
    return [...this.records];
  }

  /* ────────────────────── 门禁与执行 ────────────────────── */

  /**
   * 这条命令现在能不能发、为什么不能发、发了要不要二次确认。
   *
   * 界面把它用在两处：禁用按钮时显示 `blockers` 的原因；点了危险命令时
   * 决定要不要弹确认框（`needsConfirmation`）。管理器自己**不弹窗**。
   */
  check(request: CommandRequest): CommandGateResult {
    return this.plan(request).gate;
  }

  /**
   * 下发一条命令。
   *
   * 返回的 Promise 在**有结论**时兑现：成功、被拒绝、超时、被门禁拦下……
   * 永远不会 reject —— "命令没能执行"是一种结果，不是异常。
   *
   * 同编号同参数的重复调用（连点两下）返回**同一个 Promise**，只发一帧。
   */
  execute(request: CommandRequest): Promise<CommandResult> {
    const plan = this.plan(request);

    // 去重必须在门禁之前：连点第二下时"同编号命令正在飞"正是第一下造成的，
    // 按门禁拦下会让用户看到一个莫名其妙的失败提示
    const existing = this.pending.get(plan.key);
    if (existing !== undefined) return existing;

    if (!plan.gate.allowed || plan.command === null) return Promise.resolve(this.reject(plan));

    let settle!: (result: CommandResult) => void;
    const promise = new Promise<CommandResult>((resolve) => {
      settle = resolve;
    });

    const queueCommand = plan.command;
    const item: QueueItem = {
      id: this.nextId,
      key: plan.key,
      command: queueCommand,
      meta: plan.meta,
      params: plan.params.values,
      provided: plan.params.provided,
      frameValue: plan.frameValue,
      timeoutMs:
        request.timeoutMs ??
        (KNOWN_LONG_RUNNING.has(queueCommand) ? this.longCommandAckTimeoutMs : this.ackTimeoutMs),
      maxRetries: request.maxRetries ?? this.maxRetries,
      startedAt: this.now(),
      knownLong: KNOWN_LONG_RUNNING.has(queueCommand),
      viaInt: plan.gate.viaInt,
      attempts: 0,
      confirmation: 0,
      lastResult: null,
      progress: null,
      ackWaiter: null,
      timer: null,
      slotHeld: false,
      inProgress: false,
      status: null,
      reasons: [],
      concluded: false,
      done: false,
      resolve: settle,
    };
    this.nextId += 1;

    this.pending.set(item.key, promise);
    this.queue.push(item);
    this.pump();
    return promise;
  }

  /* ────────────────────── 能力位 ────────────────────── */

  /**
   * 主动问一次飞控的协议能力（请对端发 `AUTOPILOT_VERSION`）。
   *
   * 有些链路要先请求才推这个流；界面在"高精度坐标不可用"的提示旁边放一个按钮调它，
   * 比让用户重新插拔链路现实。有节流；返回 `false` 表示被节流挡住、链路没打开、
   * 或协议表里查不到消息 ID —— 都不是错误。
   */
  async requestCapabilities(): Promise<boolean> {
    if (this.disposed) return false;
    const at = this.now();
    if (
      this.capabilitiesRequestedAt !== null &&
      at - this.capabilitiesRequestedAt < CAPABILITIES_REQUEST_THROTTLE_MS
    ) {
      return false;
    }
    const messageId = this.autopilotVersionMessageId;
    const command = commandId('REQUEST_MESSAGE');
    if (messageId === null || command === undefined) return false;
    const linkState = this.vehicle.session.state;
    if (linkState !== 'online' && linkState !== 'silent') return false;

    // 与所有发送一样先让出调用栈（理由见 `send()`）
    await Promise.resolve();
    if (this.disposed) return false;
    try {
      // 这条请求不进队列、也不参与重传：它只是"顺手问一句"，丢了就丢了。
      // 它的 ACK（command = REQUEST_MESSAGE）也不会与队列里的命令混淆 —— 命令号不同。
      // 用会话的 sendCommandLong 而不是自己组帧：这里**不需要**重传计数
      // （confirmation 固定 0 正合适），它还会把用不到的参数位补零 ——
      // MAVLink 的 COMMAND_LONG 里"没给出的基础字段"是编码错误，不能少给
      const sent = await this.vehicle.session.sendCommandLong(command, [messageId], {
        systemId: this.vehicle.systemId,
        componentId: this.vehicle.componentId,
      });
      if (!sent) return false;
      this.capabilitiesRequestedAt = at;
      return true;
    } catch {
      return false;
    }
  }

  /** 取消全部订阅并了结所有未完成的命令。 */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const off of this.unsubscribes) off();
    this.unsubscribes.length = 0;

    // 正在等失败原因的命令先兑现（否则它们的定时器会留在这里，调用方也永远等不到结论）
    for (const window of this.reasonWindows.splice(0)) {
      this.timers.clear(window.handle);
      this.conclude(window.item);
    }

    const outstanding = [...this.queue];
    if (this.active !== null) outstanding.push(this.active);
    outstanding.push(...this.awaiting.values());
    this.queue.length = 0;
    // 逐个了结：飞控可能已经执行了它们，所以结论是"结果未知"而不是"失败"
    for (const item of outstanding) this.finish(item, 'disposed');

    this.events.clear();
  }

  /* ────────────────────── 内部：判定 ────────────────────── */

  private plan(request: CommandRequest): CommandPlan {
    const meta = commandOf(request.command) ?? null;
    const command =
      meta?.id ??
      (typeof request.command === 'number' && Number.isFinite(request.command)
        ? request.command
        : null);
    const params = normalizeParams(request.params);
    const altitudeFrame = request.altitudeFrame ?? 'relative';

    // 去重键刻意不含"最终走 INT 还是 LONG"：那是管理器按能力位算出来的结果，
    // 把计算结果放进键只会让同一个请求算出两个键（能力位一帧之内不会变）
    const key = `${command ?? `ref:${String(request.command)}`}|${paramKey(params)}|${
      request.forceLong === true ? 'long' : 'auto'
    }`;

    const busy = command === null ? undefined : this.itemFor(command);
    const frameValue = mavFrameValue(altitudeFrame);
    const state = this.vehicle.state;

    const evaluation = evaluateGate({
      state,
      kind: vehicleKindOfState(state),
      meta,
      request,
      commandIntAvailable: this.supportsCommandInt() && frameValue !== null,
      commandNumberBusy: busy !== undefined && busy.key !== key,
      enforceConfirmation: this.enforceConfirmation,
      disposed: this.disposed,
    });

    const gate: CommandGateResult = Object.freeze({
      allowed: evaluation.blockers.length === 0,
      command,
      commandName: meta?.entryName ?? null,
      label: meta?.label ?? null,
      meta,
      risk: meta?.risk ?? null,
      needsConfirmation: meta !== null && needsConfirmation(meta.risk),
      viaInt: evaluation.viaInt,
      blockers: Object.freeze([...evaluation.blockers]),
      warnings: Object.freeze([...evaluation.warnings]),
    });

    return { meta, command, params, key, altitudeFrame, frameValue, gate };
  }

  private reject(plan: CommandPlan): CommandResult {
    const blockers = plan.gate.blockers;
    const id = this.nextId;
    this.nextId += 1;
    const text = blockers.length > 0 ? blockers.map((blocker) => blocker.text).join('；') : '被门禁拦下';

    const result: CommandResult = Object.freeze({
      id,
      command: plan.command,
      commandName: plan.meta?.entryName ?? null,
      label: plan.meta?.label ?? null,
      risk: plan.meta?.risk ?? null,
      params: Object.freeze([...plan.params.values]),
      status: 'blocked',
      ok: false,
      text,
      reasons: EMPTY_REASONS,
      mavResult: null,
      mavResultName: null,
      attempts: 0,
      progress: null,
      resultParam2: null,
      viaInt: false,
      durationMs: 0,
      at: this.now(),
      blockers: Object.freeze([...blockers]),
    });

    this.record(result);
    this.events.emit('rejected', {
      id,
      command: plan.command,
      commandName: plan.meta?.entryName ?? null,
      label: plan.meta?.label ?? null,
      blockers: result.blockers,
      text,
      at: result.at,
    });
    return result;
  }

  /* ────────────────────── 内部：队列与重试 ────────────────────── */

  /** 队列推进：把队首送进串行槽。 */
  private pump(): void {
    if (this.disposed || this.active !== null) return;
    const next = this.queue.shift();
    if (next === undefined) return;
    this.active = next;
    next.slotHeld = true;
    void this.run(next);
    this.events.emit('queue', this.queueSnapshot());
  }

  private async run(item: QueueItem): Promise<void> {
    let timeoutBudget = item.maxRetries;
    let temporaryBudget = this.maxTemporaryRetries;
    let formatCorrections = 1;

    for (;;) {
      // 命令可能在等待期间被 `dispose()` 了结：那时不能再发、也不能再重传
      if (item.done) return;
      item.attempts += 1;
      item.confirmation = item.attempts - 1;
      const waiter = this.waitForAck(item, item.timeoutMs);
      const sent = await this.send(item);
      if (!sent) {
        this.abandonAckWait(item);
        this.finish(item, 'send-error');
        return;
      }
      this.events.emit('sent', {
        id: item.id,
        command: item.command,
        commandName: item.meta?.entryName ?? null,
        label: item.meta?.label ?? null,
        params: [...item.params],
        attempt: item.attempts,
        confirmation: item.confirmation,
        viaInt: item.viaInt,
        at: this.now(),
      });

      // 已知耗时的命令：发出去就让出串行槽（见 `KNOWN_LONG_RUNNING` 的说明）
      if (item.knownLong && item.slotHeld) this.detach(item);

      const ack = await waiter;
      if (item.done) return;
      if (ack === null) {
        if (timeoutBudget <= 0) {
          this.finish(item, 'no-ack');
          return;
        }
        timeoutBudget -= 1;
        this.emitRetrying(
          item,
          'timeout',
          `等待确认超时（${item.timeoutMs}ms），第 ${item.attempts} 次重传`,
        );
        continue;
      }

      const action = this.consumeAck(item, ack);
      switch (action.kind) {
        case 'done':
          return;
        case 'wait':
          return await this.awaitLongTask(item);
        case 'correct-format':
          if (formatCorrections <= 0) {
            this.finish(item, action.fallbackStatus);
            return;
          }
          formatCorrections -= 1;
          item.viaInt = action.viaInt;
          this.emitRetrying(item, 'format-switch', action.note);
          continue;
        case 'temp-retry':
          if (temporaryBudget <= 0) {
            this.finish(item, 'temporarily-rejected');
            return;
          }
          temporaryBudget -= 1;
          this.emitRetrying(item, 'temporarily-rejected', '飞控暂时拒绝，重发一次');
          continue;
      }
    }
  }

  /** 把一帧确认消化成"下一步做什么"。这是整个重试策略的唯一分叉点。 */
  private consumeAck(item: QueueItem, ack: CommandAckEvent): AckAction {
    item.lastResult = ack;
    this.noteProgress(item, ack.progress);

    const info = mavResultInfo(ack.result);
    this.events.emit('acked', {
      id: item.id,
      command: item.command,
      commandName: item.meta?.entryName ?? null,
      label: item.meta?.label ?? null,
      mavResult: ack.result,
      mavResultName: mavResultName(ack.result),
      status: info.status,
      text: info.text,
      progress: ack.progress ?? null,
      resultParam2: ack.resultParam2,
      durationMs: this.now() - item.startedAt,
      at: ack.at,
    });

    switch (info.behavior) {
      case 'accepted':
        this.finish(item, 'accepted');
        return { kind: 'done' };
      case 'in-progress':
        this.events.emit('progress', {
          id: item.id,
          command: item.command,
          commandName: item.meta?.entryName ?? null,
          label: item.meta?.label ?? null,
          progress: ack.progress ?? null,
          mavResult: ack.result,
          at: ack.at,
        });
        return { kind: 'wait' };
      case 'retry':
        return { kind: 'temp-retry' };
      case 'needs-long':
        // 只有"我们发了 INT"才需要换：飞控明说这条命令只吃 float 参数
        if (item.viaInt) {
          return {
            kind: 'correct-format',
            viaInt: false,
            note: '飞控要求仅以 COMMAND_LONG 下发，改用 COMMAND_LONG 重发',
            fallbackStatus: 'long-only',
          };
        }
        this.finish(item, 'long-only');
        return { kind: 'done' };
      case 'needs-int':
        if (!item.viaInt && this.canSendInt(item)) {
          return {
            kind: 'correct-format',
            viaInt: true,
            note: '飞控要求仅以 COMMAND_INT 下发，改用 COMMAND_INT 重发（坐标不再丢精度）',
            fallbackStatus: 'int-only',
          };
        }
        this.finish(item, 'int-only');
        return { kind: 'done' };
      default:
        this.finish(item, info.status);
        return { kind: 'done' };
    }
  }

  /**
   * 长任务等待：飞控回了 `IN_PROGRESS`，之后只等最终确认。
   *
   * 这里**不重传**：重发一条"正在执行"的命令可能让对端再跑一遍（再校准一次）。
   * 单次窗口内的静默不算失败（校准几十秒不报进度很正常），只有超过绝对上限才收尾，
   * 且结论用 `in-progress` 而不是 `failed`。
   */
  private async awaitLongTask(item: QueueItem): Promise<void> {
    if (item.slotHeld) this.detach(item);
    item.inProgress = true;
    const deadline = this.now() + this.longTaskTimeoutMs;

    for (;;) {
      const remaining = deadline - this.now();
      if (remaining <= 0) {
        this.finish(item, 'in-progress');
        return;
      }
      const waiter = this.waitForAck(item, Math.max(1, Math.min(item.timeoutMs, remaining)));
      const ack = await waiter;
      if (item.done) return;
      if (ack === null) continue;

      item.lastResult = ack;
      this.noteProgress(item, ack.progress);
      const info = mavResultInfo(ack.result);
      this.events.emit('acked', {
        id: item.id,
        command: item.command,
        commandName: item.meta?.entryName ?? null,
        label: item.meta?.label ?? null,
        mavResult: ack.result,
        mavResultName: mavResultName(ack.result),
        status: info.status,
        text: info.text,
        progress: ack.progress ?? null,
        resultParam2: ack.resultParam2,
        durationMs: this.now() - item.startedAt,
        at: ack.at,
      });

      if (info.behavior === 'in-progress') {
        this.events.emit('progress', {
          id: item.id,
          command: item.command,
          commandName: item.meta?.entryName ?? null,
          label: item.meta?.label ?? null,
          progress: ack.progress ?? null,
          mavResult: ack.result,
          at: ack.at,
        });
        continue;
      }
      // 长任务期间的任何终态都直接采信（`temporarily-rejected` 也算终态：
      // 命令已经在执行了，再重发一次只会更糟）
      this.finish(item, info.behavior === 'accepted' ? 'accepted' : info.status);
      return;
    }
  }

  private canSendInt(item: QueueItem): boolean {
    return this.supportsCommandInt() && item.frameValue !== null;
  }

  /**
   * 记下进度，但只记正数。
   *
   * `COMMAND_ACK.progress` 是扩展字段：发信方没填时会被零填充成 0，
   * 所以线上的 0 既可能是"刚开始（0%）"也可能是"这条确认不带进度"。
   * 两种含义无法区分，而"进度条从 60% 跳回 0%"是明确有害的，
   * 所以这里只接受正数：最后一次有意义的进度会被保留到命令结束。
   */
  private noteProgress(item: QueueItem, progress: number | undefined): void {
    if (progress !== undefined && progress > 0) item.progress = progress;
  }

  private emitRetrying(
    item: QueueItem,
    reason: 'timeout' | 'temporarily-rejected' | 'format-switch',
    text: string,
  ): void {
    this.events.emit('retrying', {
      id: item.id,
      command: item.command,
      commandName: item.meta?.entryName ?? null,
      label: item.meta?.label ?? null,
      attempt: item.attempts + 1,
      // 重发时 confirmation 递增：对端靠它认出"这是重传"而不是"用户又按了一次"
      confirmation: item.attempts,
      reason,
      text,
      at: this.now(),
    });
  }

  /* ────────────────────── 内部：组帧与发送 ────────────────────── */

  private async send(item: QueueItem): Promise<boolean> {
    // 与 `Vehicle.sendCommandLong()` 同一条约定：发送前先让出调用栈。
    // 上层几乎都是在帧回调里触发指令（收到一侧的事件就回一条），若在同一个栈里
    // 把帧写出去，解析器会在解析到一半时被再灌一次数据（见 vehicle.ts 的说明）。
    await Promise.resolve();
    if (this.disposed) return false;

    const session = this.vehicle.session;
    const linkState = session.state;
    // 'idle'/'connecting'/'error' 时链路写不出去；'silent' 是"链路已开但还没收到遥测"，
    // 那时发指令是合法的（正是在请对端说点什么）
    if (linkState === 'idle' || linkState === 'connecting' || linkState === 'error') return false;

    try {
      if (item.viaInt && item.frameValue !== null) {
        await session.send('COMMAND_INT', this.intValues(item, item.frameValue));
      } else {
        await session.send('COMMAND_LONG', this.longValues(item));
      }
      return true;
    } catch {
      // 组帧失败（字段类型不合法）或链路刚断：都不是异常，按"没发出去"处理
      return false;
    }
  }

  private longValues(item: QueueItem): Record<string, number> {
    const values: Record<string, number> = {
      target_system: this.vehicle.systemId,
      target_component: this.vehicle.componentId,
      command: item.command,
      // 重传计数：首次 0，每次重传 +1。**不要**在这里做任何"是否已经确认过"的判断
      confirmation: item.confirmation,
    };
    for (let index = 0; index < 7; index += 1) values[`param${index + 1}`] = item.params[index] ?? 0;
    return values;
  }

  /**
   * `COMMAND_INT` 的字段映射：`param5/6/7`（纬度/经度/高度）→ `x/y/z`。
   *
   * 精度就发生在这一行：x/y 是 `int32` 的"度 ×1e7"，能逐位无损地带走界面上的坐标；
   * `COMMAND_LONG` 的同两个参数是 `float32`，在这个量级上的步长约 1e-5 度（≈1.1 米）。
   * `param1`…`param4` 在两种帧里同名同义，直接搬过去。
   */
  private intValues(item: QueueItem, frame: number): Record<string, number> {
    return {
      target_system: this.vehicle.systemId,
      target_component: this.vehicle.componentId,
      command: item.command,
      frame,
      // current/autocontinue 是任务项语义，单独下发时必须为 0
      current: 0,
      autocontinue: 0,
      param1: item.params[0] ?? 0,
      param2: item.params[1] ?? 0,
      param3: item.params[2] ?? 0,
      param4: item.params[3] ?? 0,
      x: scaledLatLon(item.params[4] ?? Number.NaN, item.provided[4] === true),
      y: scaledLatLon(item.params[5] ?? Number.NaN, item.provided[5] === true),
      z: item.params[6] ?? 0,
    };
  }

  /* ────────────────────── 内部：ACK 与定时器 ────────────────────── */

  private itemFor(command: number): QueueItem | undefined {
    if (this.active !== null && this.active.command === command) return this.active;
    return this.awaiting.get(command);
  }

  private waitForAck(item: QueueItem, ms: number): Promise<CommandAckEvent | null> {
    return new Promise<CommandAckEvent | null>((resolve) => {
      // 等待器在**发送之前**就装好：反过来（先发再等）会漏掉紧接着回来的确认帧，
      // 表现为"偶发地把已确认的命令又重传一次" —— 结果看起来是对的，最难查
      item.ackWaiter = resolve;
      item.timer = this.timers.set(() => {
        item.ackWaiter = null;
        item.timer = null;
        resolve(null);
      }, ms);
    });
  }

  private abandonAckWait(item: QueueItem): void {
    const waiter = item.ackWaiter;
    item.ackWaiter = null;
    this.clearTimer(item);
    waiter?.(null);
  }

  private clearTimer(item: QueueItem): void {
    if (item.timer === null) return;
    this.timers.clear(item.timer);
    item.timer = null;
  }

  private handleAck(event: CommandAckEvent): void {
    const item = this.itemFor(event.command);
    if (item === undefined) return;
    const waiter = item.ackWaiter;
    // 不在等待的 ACK 直接丢掉：它只可能是"上一轮已经超时"之后迟到的帧
    if (waiter === null) return;
    item.ackWaiter = null;
    this.clearTimer(item);
    waiter(event);
  }

  private handleFrame(frame: MavlinkFrame): void {
    if (frame.messageId !== this.autopilotVersionMessageId) return;
    const definition = messageById(frame.messageId);
    if (definition === undefined) return;
    try {
      const values = decodePayload(definition, frame.payload);
      // capabilities 是 uint64 → 解码成 bigint；能力位只有 40 位左右，转 number 无风险
      const bits = toNumber(values['capabilities']);
      if (bits === null) return;
      const changed = bits !== this.capabilityBits;
      this.capabilityBits = bits;
      if (changed) {
        this.events.emit('capabilities', {
          capabilities: this.capabilities(),
          commandInt: this.supportsCommandInt(),
          at: this.now(),
        });
      }
    } catch {
      // 载荷长度不对：忽略这一帧，能力位保持原值（宁可退回 COMMAND_LONG 也不要猜）
    }
  }

  /* ────────────────────── 内部：槽位与收尾 ────────────────────── */

  /**
   * 让出串行槽，但继续跟这条命令的确认。
   *
   * 不是"直接放掉"：`COMMAND_ACK` 只带命令号，还得靠这张表把后续的
   * `IN_PROGRESS`/终态路由回正确的等待者，否则长任务永远等不到结果。
   */
  private detach(item: QueueItem): void {
    if (this.active !== item) return;
    this.active = null;
    item.slotHeld = false;
    this.awaiting.set(item.command, item);
    this.pump();
    this.events.emit('queue', this.queueSnapshot());
  }

  private finish(item: QueueItem, status: CommandStatus): void {
    if (item.done) return;
    item.done = true;
    item.status = status;
    this.abandonAckWait(item);
    if (this.awaiting.get(item.command) === item) this.awaiting.delete(item.command);
    if (this.active === item) {
      this.active = null;
      item.slotHeld = false;
    }

    // 先把串行槽让出去：等待"一句解释"不该拖住后面的命令
    this.pump();

    // 失败时留一个短窗口收集飞控的解释（`STATUSTEXT`），再兑现结论
    if (this.statustextGraceMs > 0 && REASON_CORRELATED.has(status)) {
      this.openReasonWindow(item);
      return;
    }
    this.conclude(item);
  }

  /**
   * 开始收集失败原因。
   *
   * 两个方向都要看：
   * - **向过去**：ArduPilot 在同一个处理函数里先 `send_text()` 再返回失败码，
   *   所以那条文本在线上通常**早于** ACK 到达，只看未来会漏掉最常见的形态；
   * - **向未来**：文本可能被排在 ACK 之后（固件顺序不同、或者中间夹了别的下行帧）。
   *
   * 窗口刻意很短（默认前 400ms / 后 300ms）：在这段时间里只可能有"同一件事"的两帧，
   * 而用户下一条命令最少也要几百毫秒之后。窗口开大了，就会出现"操作员刚收到
   * 一句无关的 PreArm 提示，界面上却把它算成了上一条命令失败的原因"。
   */
  private openReasonWindow(item: QueueItem): void {
    const anchor = item.lastResult?.at ?? this.now();
    const since = anchor - this.statustextLookbackMs;
    const claimed = new Set<CommandReason>();
    for (const reason of this.recentStatustext) {
      if (reason.at < since) continue;
      if (this.attachReason(item, reason)) claimed.add(reason);
    }
    // 认领过的解释从缓冲里移除（理由见 handleStatustext：一句解释只归一条命令）
    if (claimed.size > 0) {
      const kept = this.recentStatustext.filter((reason) => !claimed.has(reason));
      this.recentStatustext.length = 0;
      this.recentStatustext.push(...kept);
    }

    const handle = this.timers.set(() => {
      const index = this.reasonWindows.findIndex((window) => window.item === item);
      if (index >= 0) this.reasonWindows.splice(index, 1);
      this.conclude(item);
    }, this.statustextGraceMs);
    this.reasonWindows.push({ item, handle });
  }

  private handleStatustext(event: StatustextEntry): void {
    // INFO/DEBUG 是例行播报（"Flight mode: LOITER"），不是拒绝理由
    if (event.severity > REASON_MAX_SEVERITY) return;
    const reason: CommandReason = Object.freeze({
      text: event.text,
      severity: event.severity,
      severityName: event.severityName,
      at: event.at,
    });

    // 先给已经开着窗口的命令（刚失败、正等着解释的那几条）
    let claimed = false;
    for (const window of [...this.reasonWindows]) {
      if (this.attachReason(window.item, reason)) claimed = true;
    }
    // 已经被某条命令认领的解释不再进缓冲：否则它还会被随后失败的另一条命令
    // 通过"向前关联"再认领一次，同一句话挂到两条命令上是典型的过度关联
    if (claimed) return;

    this.recentStatustext.push(reason);
    if (this.recentStatustext.length > STATUSTEXT_BUFFER_LIMIT) this.recentStatustext.shift();
  }

  /** 记下一条原因。返回是否真的收下了（用于"一句解释只归一条命令"的判定）。 */
  private attachReason(item: QueueItem, reason: CommandReason): boolean {
    if (item.reasons.some((existing) => existing.text === reason.text)) return false;
    // 分块播报会把"累积后的整句"再发一遍（见 vehicle.ts 的 pushStatustext）：
    // 用更完整的版本替换掉片段，而不是两条都留着
    const partial = item.reasons.findIndex((existing) => reason.text.startsWith(existing.text));
    if (partial >= 0) {
      item.reasons[partial] = reason;
      return true;
    }
    item.reasons.push(reason);
    return true;
  }

  /** 兑现结论：构造结果、写历史、发事件、resolve。 */
  private conclude(item: QueueItem): void {
    if (item.concluded) return;
    item.concluded = true;

    const result = this.buildResult(item, item.status ?? 'unknown-result');
    this.pending.delete(item.key);
    this.record(result);

    if (!result.ok) {
      this.events.emit('failed', {
        id: item.id,
        command: item.command,
        commandName: item.meta?.entryName ?? null,
        label: item.meta?.label ?? null,
        result,
        at: result.at,
      });
    }
    this.events.emit('queue', this.queueSnapshot());
    item.resolve(result);
  }

  private buildResult(item: QueueItem, status: CommandStatus): CommandResult {
    const ack = item.lastResult;
    const info = ack === null ? null : mavResultInfo(ack.result);
    const base =
      info !== null && !LOCALLY_CONCLUDED.has(status) ? info.text : this.statusText(item, status);

    return Object.freeze({
      id: item.id,
      command: item.command,
      commandName: item.meta?.entryName ?? null,
      label: item.meta?.label ?? null,
      risk: item.meta?.risk ?? null,
      params: Object.freeze([...item.params]),
      status,
      ok: status === 'accepted',
      // 有飞控的解释时，用它把结论说具体："执行失败：runup not complete"
      text:
        item.reasons.length > 0
          ? `${COMMAND_STATUS_LABELS[status]}：${item.reasons.map((reason) => reason.text).join('；')}`
          : base,
      reasons: Object.freeze([...item.reasons]),
      mavResult: ack?.result ?? null,
      mavResultName: ack === null ? null : mavResultName(ack.result),
      attempts: item.attempts,
      progress: item.progress,
      resultParam2: ack?.resultParam2 ?? null,
      viaInt: item.viaInt,
      durationMs: this.now() - item.startedAt,
      at: this.now(),
      blockers: EMPTY_BLOCKERS,
    });
  }

  /** 非 `MAV_RESULT` 的结论文案（本地判定）。 */
  private statusText(item: QueueItem, status: CommandStatus): string {
    const name = item.meta?.label ?? item.meta?.entryName ?? `命令 ${item.command}`;
    switch (status) {
      case 'no-ack':
        return `未收到确认：${name} 已发送 ${item.attempts} 次（含 ${
          item.attempts - 1
        } 次重传），飞控始终没有回 COMMAND_ACK`;
      case 'send-error':
        return `发送失败：${name} 没有发出去（链路未打开或组帧出错）`;
      case 'in-progress':
        return `${name} 仍在执行中，但已超过 ${Math.round(
          this.longTaskTimeoutMs / 60_000,
        )} 分钟没有新的确认（链路可能已中断）—— 无法确认它是否完成`;
      case 'long-only':
        return `${name} 只能以 COMMAND_LONG 下发，且这次无法改用 COMMAND_LONG 重发`;
      case 'int-only':
        return `${name} 只能以 COMMAND_INT 下发（飞控未上报 COMMAND_INT 能力，或坐标框架无法确定），本次无法重发`;
      case 'disposed':
        return `管理器已销毁，${name} 的结果不再被跟踪 —— 飞控可能已经执行了它，请以机体实际状态为准`;
      default:
        return `${name} 没有成功执行（${status}）`;
    }
  }

  private record(result: CommandResult): void {
    this.records.unshift(
      Object.freeze({
        id: result.id,
        command: result.command,
        commandName: result.commandName,
        label: result.label,
        risk: result.risk,
        params: result.params,
        status: result.status,
        ok: result.ok,
        text: result.text,
        reasons: result.reasons,
        mavResult: result.mavResult,
        attempts: result.attempts,
        durationMs: result.durationMs,
        viaInt: result.viaInt,
        at: result.at,
      }),
    );
    if (this.records.length > this.historyLimit) this.records.length = this.historyLimit;
  }

  private describeItem(item: QueueItem): CommandQueueEntry {
    const state: CommandQueueState = item.inProgress
      ? 'long-running'
      : item.slotHeld
        ? 'active'
        : item.attempts === 0
          ? 'queued'
          : 'awaiting-ack';
    return {
      id: item.id,
      command: item.command,
      commandName: item.meta?.entryName ?? null,
      label: item.meta?.label ?? null,
      risk: item.meta?.risk ?? null,
      state,
      attempts: item.attempts,
      confirmation: item.confirmation,
      enqueuedAt: item.startedAt,
      waitingMs: this.now() - item.startedAt,
      viaInt: item.viaInt,
    };
  }
}
